// Planner V2 — port limpo do generatePlan() do Robox 1.0 (sem DOM, sem eventos de trabalho)
// Distribui conteúdos pendentes em sessões ao longo dos dias de estudo.

import type { Content, RoutineEvent } from '@app-types/domain';
import { routineBlocksForDate } from './routines';

export interface PlannerParams {
  /** ISO date (yyyy-mm-dd) do primeiro dia possível */
  start_date: string;
  /** dias da semana de estudo: 0=dom .. 6=sáb */
  study_days: number[];
  /** horas de estudo por dia */
  hours_per_day: number;
  /** tamanho máximo de cada sessão (min) */
  block_min: number;
  /** intervalo entre sessões (min) */
  break_min: number;
  /** horário de início do estudo "HH:MM" */
  start_time: string;
}

export const DEFAULT_PLANNER_PARAMS: PlannerParams = {
  start_date: new Date().toISOString().slice(0, 10),
  study_days: [1, 2, 3, 4, 5],
  hours_per_day: 2,
  block_min: 25,
  break_min: 10,
  start_time: '19:00',
};

export interface PlannedSessionDraft {
  content_id: string;
  goal_id?: string;
  planned_at: string; // ISO datetime
  planned_duration_min: number;
}

export type Viability = 'ok' | 'ajustado' | 'inviavel' | 'sem_prazo';

export interface PlanDiagnostic {
  total_min: number;
  weekly_capacity_min: number;
  weeks_needed: number;
  sessions_count: number;
  end_date: string | null; // data da última sessão
  unplaced_contents: number;
  viability: Viability;
  /** margem de folga vs prazo (0.2 = 20% de sobra). null sem prazo */
  margin: number | null;
}

export interface PlanResult {
  sessions: PlannedSessionDraft[];
  diagnostic: PlanDiagnostic;
}

const MIN_SESSION = 5; // evita fragmento < 5min quando conteúdo ainda tem saldo

function atTime(dateIso: string, hhmm: string): Date {
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(`${dateIso}T00:00:00`);
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
}

function isoDateLocal(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

/**
 * Gera plano sequencial: fila cronológica de conteúdos 'todo',
 * divididos em blocos de até block_min, preenchendo os dias de estudo.
 * Desvia dos blocos ocupados da rotina (sono, trabalho, etc).
 */
export function generatePlan(
  contents: Content[],
  params: PlannerParams,
  targetDateIso?: string,
  routines: RoutineEvent[] = [],
): PlanResult {
  const queue = contents
    .filter((c) => c.status === 'todo' || c.status === 'doing')
    .sort((a, b) => a.order - b.order)
    .map((c) => ({
      id: c.id,
      goalId: c.goalId,
      remainingMin: Math.max(1, Math.round(c.duration_min)),
    }));

  const totalMin = queue.reduce((s, q) => s + q.remainingMin, 0);
  const dailyMin = params.hours_per_day * 60;
  const weeklyCapacityMin = params.study_days.length * dailyMin;

  const sessions: PlannedSessionDraft[] = [];
  let qIdx = 0;

  // horizonte: semanas necessárias + 4 de buffer, mínimo 8 semanas
  const weeksNeeded = weeklyCapacityMin > 0 ? Math.ceil(totalMin / weeklyCapacityMin) : 0;
  const horizonDays = Math.max(8, weeksNeeded + 4) * 7;

  const start = new Date(`${params.start_date}T00:00:00`);

  const [sh, sm] = params.start_time.split(':').map(Number);
  const startMin0 = (sh || 0) * 60 + (sm || 0);

  for (let offset = 0; offset <= horizonDays && qIdx < queue.length; offset++) {
    const day = new Date(start);
    day.setDate(start.getDate() + offset);
    if (!params.study_days.includes(day.getDay())) continue;

    const dayIso = isoDateLocal(day);
    const busy = routineBlocksForDate(routines, dayIso);
    let cursorMin = startMin0;
    const dayEndMin = startMin0 + dailyMin;
    let safety = 0;

    while (cursorMin < dayEndMin && qIdx < queue.length && safety < 200) {
      safety++;
      const item = queue[qIdx];
      const minutesLeft = dayEndMin - cursorMin;
      const sessionMin = Math.min(item.remainingMin, params.block_min, minutesLeft);
      if (sessionMin <= 0) break;
      if (sessionMin < MIN_SESSION && item.remainingMin > sessionMin) break; // empurra pro próximo dia

      // conflita com rotina? pula pro fim do bloco ocupado
      const conflict = busy.find(
        (b) => cursorMin < b.end_min && cursorMin + sessionMin > b.start_min,
      );
      if (conflict) {
        cursorMin = Math.max(cursorMin + 1, conflict.end_min);
        continue;
      }

      const plannedAt = new Date(atTime(dayIso, '00:00').getTime() + cursorMin * 60000);
      sessions.push({
        content_id: item.id,
        goal_id: item.goalId,
        planned_at: plannedAt.toISOString(),
        planned_duration_min: sessionMin,
      });

      item.remainingMin -= sessionMin;
      if (item.remainingMin <= 0) qIdx++;
      cursorMin += sessionMin + params.break_min;
    }
  }

  const endDate =
    sessions.length > 0 ? isoDateLocal(new Date(sessions[sessions.length - 1].planned_at)) : null;

  // Viabilidade vs prazo
  let viability: Viability = 'sem_prazo';
  let margin: number | null = null;
  if (targetDateIso && endDate) {
    const target = new Date(`${targetDateIso}T23:59:59`).getTime();
    const startMs = start.getTime();
    const endMs = new Date(`${endDate}T00:00:00`).getTime();
    const available = Math.max(1, target - startMs);
    const used = endMs - startMs;
    margin = (available - used) / available;
    viability = endMs > target ? 'inviavel' : margin < 0.2 ? 'ajustado' : 'ok';
  }

  return {
    sessions,
    diagnostic: {
      total_min: totalMin,
      weekly_capacity_min: weeklyCapacityMin,
      weeks_needed: weeksNeeded,
      sessions_count: sessions.length,
      end_date: endDate,
      unplaced_contents: queue.length - qIdx,
      viability,
      margin,
    },
  };
}
